import { useCallback } from 'react';

import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from 'redux/store/store.types';
import { EEnergySource, EProductionTechnology, ETypeOfSupport } from 'types';
import { getApiErrorMessage } from 'utils';
import { formatGoUnit } from 'utils';

import { EForwardStatus, EnergySourceTypeMap, ForwardStatusMap, TechnologyTypeMap } from 'modules/eecs-go';

import {
  useLazyFetchDashboardQuery,
  useLazyFetchForwardExchangeChartQuery,
  useLazyFetchForwardStaticChartQuery,
  useLazyFetchForwardVolumeChartQuery,
  useLazyFetchPredictionStaticChartQuery,
  useLazyFetchPredictionVolumeChartQuery,
  useLazyFetchPredictionYearsQuery,
  useLazyFetchRevenueExpenseLineChartQuery,
  useLazyFetchRevenueExpenseStaticChartQuery,
  useLazyFetchSpotExchangeChartQuery,
  useLazyFetchSpotStaticChartQuery,
  useLazyFetchSpotVolumeChartQuery,
} from '../api/eecs-dashboard.api';
import {
  dashboardColors,
  detailsAIBDefaultData,
  detailsTypeOfSupportDefaultData,
  revenueExpenseForwardDefaultData,
  revenueExpenseSpotDefaultData,
} from '../constants/eecs-dashboard.constants';
import { setAvailablePredictionYears, setDashboard, setIsLoading } from '../store/eecs-dashboard.slice';
import {
  EDashboardChartDataType,
  EDashboardItemType,
  EDashboardTabType,
  IDashboardChartDataWithDate,
  IDashboardChartLineData,
  IDashboardChartPayload,
  IDashboardChartPieData,
  IDashboardForwardStaticChartData,
  IDashboardPredictionsStaticChartData,
  IDashboardRevenueExpenseStaticChartData,
  IDashboardSpotStaticChartData,
} from '../types/eecs-dashboard.types';

const rearrangeData = (data: IDashboardChartPieData[]) => {
  const sorted = [...data].sort((a, b) => b.value + a.value);

  const result: typeof data = [];
  let left = 0;
  let right = sorted.length - 1;
  let toggle = true;

  while (left <= right) {
    if (toggle) {
      result.push(sorted[left]);
      left++;
    } else {
      result.push(sorted[right]);
      right--;
    }
    toggle = !toggle;
  }

  return result;
};

interface UseEecsDashboard {
  fetchDashboardHandler: () => void;
  fetchExchangeChartHandler: (
    payload: IDashboardChartPayload,
    tabType: EDashboardTabType.FORWARD | EDashboardTabType.SPOT,
  ) => Promise<IDashboardChartLineData | null>;
  fetchVolumeChartHandler: (
    payload: IDashboardChartPayload,
    tabType: EDashboardTabType.FORWARD | EDashboardTabType.SPOT | EDashboardTabType.PREDICTIONS,
  ) => Promise<IDashboardChartLineData | null>;
  fetchSpotStaticChartHandler: () => Promise<IDashboardSpotStaticChartData | null>;
  fetchForwardStaticChartHandler: () => Promise<IDashboardForwardStaticChartData | null>;
  fetchRevenueExpenseLineChartHandler: (payload: IDashboardChartPayload) => Promise<IDashboardChartLineData | null>;
  fetchRevenueExpenseStaticChartHandler: () => Promise<IDashboardRevenueExpenseStaticChartData | null>;
  fetchPredictionYearsHandler: () => void;
  fetchPredictionStaticChartHandler: (
    payload: IDashboardChartPayload,
  ) => Promise<IDashboardPredictionsStaticChartData | null>;
}

export const useEecsDashboard = (): UseEecsDashboard => {
  const dispatch = useAppDispatch();
  const { dashboard } = useAppSelector((store) => store.eecsDashboard);
  const [fetchDashboardQuery] = useLazyFetchDashboardQuery();
  const [fetchSpotExchangeChartQuery] = useLazyFetchSpotExchangeChartQuery();
  const [fetchSpotVolumeChartQuery] = useLazyFetchSpotVolumeChartQuery();
  const [fetchSpotStaticChartQuery] = useLazyFetchSpotStaticChartQuery();
  const [fetchForwardVolumeChartQuery] = useLazyFetchForwardVolumeChartQuery();
  const [fetchForwardExchangeChartQuery] = useLazyFetchForwardExchangeChartQuery();
  const [fetchForwardStaticChartQuery] = useLazyFetchForwardStaticChartQuery();
  const [fetchRevenueExpenseLineChartQuery] = useLazyFetchRevenueExpenseLineChartQuery();
  const [fetchRevenueExpenseStaticChartQuery] = useLazyFetchRevenueExpenseStaticChartQuery();
  const [fetchPredictionYearsQuery] = useLazyFetchPredictionYearsQuery();
  const [fetchPredictionVolumeChartQuery] = useLazyFetchPredictionVolumeChartQuery();
  const [fetchPredictionStaticChartQuery] = useLazyFetchPredictionStaticChartQuery();

  const fetchPredictionYearsHandler = async () => {
    const { data: response, error } = await fetchPredictionYearsQuery({});
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get Prediction Years!');
      return;
    }
    dispatch(setAvailablePredictionYears(response.data));
  };

  const fetchDashboardHandler = useCallback(async () => {
    dispatch(setIsLoading(true));
    const { data: response, error } = await fetchDashboardQuery({});
    dispatch(setIsLoading(false));
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get EECS Dashboard!');
      return;
    }

    const updatedDashboard = { ...dashboard };

    Object.keys(updatedDashboard).forEach((key) => {
      if (response.data[key as EDashboardItemType]) {
        updatedDashboard[key as EDashboardItemType] = {
          ...updatedDashboard[key as EDashboardItemType],
          ...response.data[key as EDashboardItemType],
        };
      }
    });

    dispatch(setDashboard(updatedDashboard));
  }, [dashboard]);

  const fetchRevenueExpenseLineChartHandler = async (payload: IDashboardChartPayload) => {
    const { data: response, error } = await fetchRevenueExpenseLineChartQuery(payload);

    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get Revenue&Expense Chart!');
      return null;
    }
    const chartData: IDashboardChartLineData = {
      diff: response.data[EDashboardChartDataType.EXPENSE]?.diff || 0,
      range: payload.range as string,
      data: [
        {
          id: 'Expense',
          data:
            response.data[EDashboardChartDataType.EXPENSE]?.data.map((item: IDashboardChartDataWithDate) => ({
              x: new Date(item.date),
              y: item.value,
            })) || [],
        },
        {
          id: 'Revenue',
          data:
            response.data[EDashboardChartDataType.REVENUE]?.data.map((item: IDashboardChartDataWithDate) => ({
              x: new Date(item.date),
              y: item.value,
            })) || [],
        },
      ],
    };
    return chartData;
  };

  const getFormattedVolume = (volume: number) => {
    return formatGoUnit(volume > 1000 ? volume / 1000 : volume, volume > 1000 ? ' GWh' : 'MWh');
  };

  const fetchExchangeChartHandler = async (
    payload: IDashboardChartPayload,
    tabType: EDashboardTabType.FORWARD | EDashboardTabType.SPOT,
  ) => {
    const routes = {
      [EDashboardTabType.SPOT]: fetchSpotExchangeChartQuery,
      [EDashboardTabType.FORWARD]: fetchForwardExchangeChartQuery,
    };
    const { data: response, error } = await routes[tabType](payload);

    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get Spot Exchange Chart!');
      return null;
    }
    const chartData: IDashboardChartLineData = {
      diff: response.data[EDashboardChartDataType.VOLUME]?.diff || 0,
      range: payload.range as string,
      data: [
        {
          id: '€',
          data:
            response.data[EDashboardChartDataType.PRICE]?.data.map((item: IDashboardChartDataWithDate) => ({
              x: new Date(item.date),
              y: item.value,
            })) || [],
        },
        {
          id: 'MWh',
          data:
            response.data[EDashboardChartDataType.VOLUME]?.data.map((item: IDashboardChartDataWithDate) => ({
              x: new Date(item.date),
              y: item.value,
            })) || [],
        },
      ],
    };
    return chartData;
  };

  const fetchVolumeChartHandler = async (
    payload: IDashboardChartPayload,
    tabType: EDashboardTabType.FORWARD | EDashboardTabType.SPOT | EDashboardTabType.PREDICTIONS,
  ) => {
    const routes = {
      [EDashboardTabType.SPOT]: fetchSpotVolumeChartQuery,
      [EDashboardTabType.FORWARD]: fetchForwardVolumeChartQuery,
      [EDashboardTabType.PREDICTIONS]: fetchPredictionVolumeChartQuery,
    };
    const { data: response, error } = await routes[tabType](payload);
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get Volume Chart!');
      return null;
    }
    const chartData: IDashboardChartLineData = {
      diff: response.data[EDashboardChartDataType.VOLUME]?.diff || 0,
      range: payload.range as string,
      data: [
        {
          id: 'Volume',
          data:
            response.data[EDashboardChartDataType.VOLUME]?.data.map((item: IDashboardChartDataWithDate) => ({
              x: new Date(item.date),
              y: item.value,
            })) || [],
        },
      ],
    };
    return chartData;
  };

  const fetchPredictionStaticChartHandler = async (payload: IDashboardChartPayload) => {
    const { data: response, error } = await fetchPredictionStaticChartQuery(payload);
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get Predictions Static Charts!');
      return null;
    }
    const chartData: IDashboardPredictionsStaticChartData = {
      predictions_percentage:
        rearrangeData(
          response.data[EDashboardChartDataType.PREDICTIONS_PERCENTAGE]?.map((item, index) => {
            const key = item.name.replaceAll(' ', '_').toLowerCase();
            return {
              id: key,
              label: item.name,
              color: dashboardColors[index % dashboardColors.length] || '#000000',
              value: item.value,
              formattedValue: item.additional_value && getFormattedVolume(item.additional_value),
            };
          }),
        ) || [],
    };
    return chartData;
  };

  const fetchSpotStaticChartHandler = async () => {
    const { data: response, error } = await fetchSpotStaticChartQuery({});
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get Spot Static Charts!');
      return null;
    }
    const chartData: IDashboardSpotStaticChartData = {
      type_of_installation:
        rearrangeData(
          response.data[EDashboardChartDataType.TYPE_OF_INSTALLATION]?.map((item) => {
            const key = item.name as EProductionTechnology;
            return {
              id: key,
              label: TechnologyTypeMap[key]?.name,
              color: TechnologyTypeMap[key]?.color || '#000000',
              value: item.value,
              formattedValue: item.additional_value && getFormattedVolume(item.additional_value),
            };
          }),
        ) || [],
      energy_source:
        rearrangeData(
          response.data[EDashboardChartDataType.ENERGY_SOURCE]?.map((item) => {
            const key = item.name as EEnergySource;
            return {
              id: key,
              label: EnergySourceTypeMap[key].name,
              color: EnergySourceTypeMap[key].color || '#000000',
              value: item.value,
              formattedValue: item.additional_value && getFormattedVolume(item.additional_value),
            };
          }),
        ) || [],
      details: [
        {
          ...detailsAIBDefaultData,
          firstVolume: response.data[EDashboardChartDataType.AIB_GRID_CONNECTED]
            ?.filter((item) => item.name === 'yes')
            .reduce((totalVolume, item) => totalVolume + item.value, 0),

          secondVolume: response.data[EDashboardChartDataType.AIB_GRID_CONNECTED]
            ?.filter((item) => item.name === 'no')
            .reduce((totalVolume, item) => totalVolume + item.value, 0),
        },
        {
          ...detailsTypeOfSupportDefaultData,
          firstVolume: response.data[EDashboardChartDataType.TYPE_OF_SUPPORT]
            ?.filter((item) => item.name === ETypeOfSupport.NO_SUPPORT)
            .reduce((totalVolume, item) => totalVolume + item.value, 0),

          secondVolume: response.data[EDashboardChartDataType.TYPE_OF_SUPPORT]
            ?.filter((item) => item.name === ETypeOfSupport.PRODUCTION)
            .reduce((totalVolume, item) => totalVolume + item.value, 0),
        },
      ],
    };
    return chartData;
  };

  const fetchForwardStaticChartHandler = async () => {
    const { data: response, error } = await fetchForwardStaticChartQuery({});
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get Forward Static Charts!');
      return null;
    }
    const chartData: IDashboardForwardStaticChartData = {
      type_of_installation:
        rearrangeData(
          response.data[EDashboardChartDataType.TYPE_OF_INSTALLATION]?.map((item) => {
            const key = item.name as EProductionTechnology;
            return {
              id: key,
              label: TechnologyTypeMap[key]?.name,
              color: TechnologyTypeMap[key]?.color || '#000000',
              value: item.value,
              formattedValue: item.additional_value && getFormattedVolume(item.additional_value),
            };
          }),
        ) || [],
      energy_source:
        rearrangeData(
          response.data[EDashboardChartDataType.ENERGY_SOURCE]?.map((item) => {
            const key = item.name as EEnergySource;
            return {
              id: key,
              label: EnergySourceTypeMap[key].name,
              color: EnergySourceTypeMap[key].color || '#000000',
              value: item.value,
              formattedValue: item.additional_value && getFormattedVolume(item.additional_value),
            };
          }),
        ) || [],
      acquired:
        rearrangeData(
          response.data[EDashboardChartDataType.ACQUIRED]?.map((item) => {
            const key = item.name as EForwardStatus;
            return {
              id: key,
              label: ForwardStatusMap[key].name,
              color: ForwardStatusMap[key].color || '#000000',
              value: item.value,
              formattedValue: item.additional_value && String(item.additional_value),
            };
          }),
        ) || [],
      scheduled:
        rearrangeData(
          response.data[EDashboardChartDataType.SCHEDULED]?.map((item) => {
            const key = item.name as EForwardStatus;
            return {
              id: key,
              label: ForwardStatusMap[key].name,
              color: ForwardStatusMap[key].color || '#000000',
              value: item.value,
              formattedValue: item.additional_value && String(item.additional_value),
            };
          }),
        ) || [],
      details: [
        {
          ...detailsAIBDefaultData,
          firstVolume: response.data[EDashboardChartDataType.AIB_GRID_CONNECTED]
            ?.filter((item) => item.name === 'yes')
            .reduce((totalVolume, item) => totalVolume + item.value, 0),

          secondVolume: response.data[EDashboardChartDataType.AIB_GRID_CONNECTED]
            ?.filter((item) => item.name === 'no')
            .reduce((totalVolume, item) => totalVolume + item.value, 0),
        },
        {
          ...detailsTypeOfSupportDefaultData,
          firstVolume: response.data[EDashboardChartDataType.TYPE_OF_SUPPORT]
            ?.filter((item) => item.name === ETypeOfSupport.NO_SUPPORT)
            .reduce((totalVolume, item) => totalVolume + item.value, 0),

          secondVolume: response.data[EDashboardChartDataType.TYPE_OF_SUPPORT]
            ?.filter((item) => item.name === ETypeOfSupport.PRODUCTION)
            .reduce((totalVolume, item) => totalVolume + item.value, 0),
        },
      ],
    };
    return chartData;
  };

  const fetchRevenueExpenseStaticChartHandler = async () => {
    const { data: response, error } = await fetchRevenueExpenseStaticChartQuery({});
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get Spot Static Charts!');
      return null;
    }
    const chartData: IDashboardRevenueExpenseStaticChartData = {
      chart_expense: [
        {
          ...revenueExpenseForwardDefaultData,
          firstVolume: response.data[EDashboardChartDataType.FORWARD].expense,
        },
        {
          ...revenueExpenseSpotDefaultData,
          firstVolume: response.data[EDashboardChartDataType.SPOT].expense,
        },
      ],
      chart_revenue: [
        {
          ...revenueExpenseForwardDefaultData,
          firstVolume: response.data[EDashboardChartDataType.FORWARD].revenue,
        },
        {
          ...revenueExpenseSpotDefaultData,
          firstVolume: response.data[EDashboardChartDataType.SPOT].revenue,
        },
      ],
    };
    return chartData;
  };

  return {
    fetchDashboardHandler,
    fetchExchangeChartHandler,
    fetchVolumeChartHandler,
    fetchSpotStaticChartHandler,
    fetchForwardStaticChartHandler,
    fetchRevenueExpenseLineChartHandler,
    fetchRevenueExpenseStaticChartHandler,
    fetchPredictionYearsHandler,
    fetchPredictionStaticChartHandler,
  };
};
